// frontend/src/pages/LoginPage.tsx
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserService } from '../../services/userService';
import { AuthService } from '../../services/authService';
import { UserContext } from '../../context/userContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/16/solid';
import { TruckIcon, ChartBarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../components/Avatar';
import { Field, Label } from '../../components/Fieldset';
import { Input } from '../../components/Input';
import { Checkbox, CheckboxField } from '../../components/Checkbox';
import { Button } from '../../components/Button';
import Loading from '../../components/Loading';
import { APP_VERSION } from '../../version';
import { ThemeToggle } from '../../components/ThemeToggle';

const FEATURES = [
    { icon: TruckIcon, label: 'Inventory & tracking across every warehouse' },
    { icon: ChartBarIcon, label: 'Live stock reports and AI-assisted analytics' },
    { icon: ShieldCheckIcon, label: 'Audited, role-based access for every user' },
];

// Only the username is ever persisted - never the password.
const REMEMBERED_USERNAME_KEY = 'gpcwms_remembered_username';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState(() => localStorage.getItem(REMEMBERED_USERNAME_KEY) || '');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(() => Boolean(localStorage.getItem(REMEMBERED_USERNAME_KEY)));
    const { setCurrentUser } = useContext(UserContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            const loginSuccess = await AuthService.login(username, password);
            if (loginSuccess) {
                if (rememberMe) {
                    localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
                } else {
                    localStorage.removeItem(REMEMBERED_USERNAME_KEY);
                }
                const userProfile = await UserService.getProfile();
                setCurrentUser(userProfile);
                navigate('/gp/dashboard');
            }
        } catch (error) {
            console.error('Login failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(prevState => !prevState);
    };

    const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const trimmedValue = value.trim();
        const sanitizedValue = trimmedValue.toLowerCase().replace(/\s/g, '');
        const sanitizedValueWithoutSpecialChars = sanitizedValue.replace(/[^\w]/g, '');
        setUsername(sanitizedValueWithoutSpecialChars);
    };

    useEffect(() => {
        document.title = "Login";
    }, []);

    return (
        <div className="flex h-dvh w-full overflow-hidden bg-card">
            <div className="absolute right-6 top-6 z-10">
                <ThemeToggle />
            </div>

            {/* Branding panel - desktop only, everything else collapses into the form panel below */}
            <div className="relative hidden w-1/2 shrink-0 flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground lg:flex xl:w-3/5">
                <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-brand-teal/20 blur-3xl" />
                <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 size-80 rounded-full bg-brand-teal/10 blur-3xl" />

                <div className="relative flex items-center gap-3">
                    <Avatar src="/images/GPCLogo2.png" className="size-10 bg-white" />
                    <span className="text-sm font-semibold tracking-wide">GPC Warehouse MS</span>
                </div>

                <div className="relative max-w-md">
                    <h1 className="text-3xl font-semibold leading-tight">
                        Global Pharma &amp; Central Pharmacy
                    </h1>
                    <p className="mt-3 text-primary-foreground/70">
                        One system to manage inventory, tracking, and reporting across every warehouse.
                    </p>
                    <ul className="mt-8 space-y-4">
                        {FEATURES.map(({ icon: Icon, label }) => (
                            <li key={label} className="flex items-center gap-3">
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10">
                                    <Icon className="size-5" />
                                </span>
                                <span className="text-sm text-primary-foreground/90">{label}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="relative text-xs text-primary-foreground/50">
                    ©Global Pharma Management System 2026 by Doxa Stack Malaysia. All rights reserved.
                    <br />
                    Version {APP_VERSION}
                </p>
            </div>

            {/* Form panel */}
            <div className="flex w-full flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-10 lg:w-1/2 xl:w-2/5">
                <div className="w-full max-w-sm">
                    <div className="mb-8 grid justify-items-center gap-3 lg:hidden">
                        <Avatar src="/images/GPCLogo2.png" className="w-40 bg-white text-gray-700" />
                        <h3 className="text-center text-sm font-semibold text-foreground">
                            Global Pharma Sdn. Bhd.<br />Central Pharmacy Sdn. Bhd.
                        </h3>
                    </div>

                    <h2 className="mb-1 text-xl font-semibold text-foreground">Welcome back</h2>
                    <p className="mb-6 text-sm text-muted-foreground">Sign in to continue to your workspace.</p>

                    <form className="space-y-5" onSubmit={handleSubmit}>
                        <Field>
                            <Label>Username</Label>
                            <Input
                                name="username"
                                type="text"
                                autoComplete="username"
                                required
                                value={username}
                                onChange={handleUsernameChange}
                            />
                        </Field>

                        <Field>
                            <Label>Password</Label>
                            <div className="relative">
                                <Input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground focus:outline-none"
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="size-4" />
                                    ) : (
                                        <EyeIcon className="size-4" />
                                    )}
                                </button>
                            </div>
                        </Field>

                        <CheckboxField>
                            <Checkbox
                                name="remember-me"
                                checked={rememberMe}
                                onChange={setRememberMe}
                            />
                            <Label>Remember me</Label>
                        </CheckboxField>

                        <div>
                            {(!loading) ? (
                                <Button type="submit" className="w-full justify-center">
                                    Login
                                </Button>
                            ) : (
                                <Button type="button" disabled className="w-full justify-center">
                                    <Loading />
                                    <span className="ml-2">Logging in...</span>
                                </Button>
                            )}
                        </div>
                    </form>

                    <p className="mt-10 text-center text-[11px] leading-5 text-muted-foreground lg:hidden">
                        ©Global Pharma Management System 2026 by Doxa Stack Malaysia. All rights reserved.
                        <br />
                        Version {APP_VERSION}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
